import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ArrowDownTrayIcon, PaperAirplaneIcon } from '@heroicons/react/24/outline';
import './App.css';
import './index.css';
import Titlebar from './Titlebar';
import TitlebarWithControls from './TitlebarWithControls';
import CustomerComboBox from './CustomerComboBox';
import { apiUrl } from './config';

function InvoiceForm() {
	const navigate = useNavigate();
	const location = useLocation();
	const { invoiceId } = useParams();
	const hasInvoiceId = Boolean(invoiceId);
	const isViewMode = location.pathname.startsWith('/invoiceview/');
	const isEditMode = hasInvoiceId && !isViewMode;

	const [isLoading, setIsLoading] = useState(hasInvoiceId);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [customers, setCustomers] = useState([]);
	const [selectedCustomer, setSelectedCustomer] = useState(null);
	const [customerComboBoxKey, setCustomerComboBoxKey] = useState(0);
	const [entryLines, setEntryLines] = useState([]);
	const [asRunLines, setAsRunLines] = useState([]);
	const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
	const [formData, setFormData] = useState({
		customer_id: '',
		sales_rep_email: '',
		name: '',
		order_campaign: '',
		due_date: '',
		status: '',
		amount_due: '',
		total_value: '',
		total_impressions: '',
	});

	const handleInputChange = (event) => {
		if (isViewMode) return;
		const { name, value } = event.target;
		setFormData((prev) => ({
			...prev,
			[name]: value,
		}));
	};

	const normalizeCurrencyInput = (value) => {
		const input = String(value ?? '');
		const cleaned = input.replace(/[^\d.]/g, '');
		const [whole = '', ...fractionParts] = cleaned.split('.');
		const fraction = fractionParts.join('');
		if (fractionParts.length === 0) {
			return whole;
		}
		return `${whole}.${fraction.slice(0, 2)}`;
	};

	const parseCurrencyValue = (value) => {
		const normalized = normalizeCurrencyInput(value);
		if (!normalized) return 0;
		const parsed = parseFloat(normalized);
		return Number.isFinite(parsed) ? parsed : 0;
	};

	const formatCurrencyValue = (value) => {
		if (value === '' || value === null || value === undefined) return '';
		const parsed = parseCurrencyValue(value);
		return new Intl.NumberFormat('en-US', {
			style: 'currency',
			currency: 'USD',
			minimumFractionDigits: 2,
			maximumFractionDigits: 2,
		}).format(parsed);
	};

	const handleCurrencyChange = (event) => {
		if (isViewMode) return;
		const { name, value } = event.target;
		setFormData((prev) => ({
			...prev,
			[name]: normalizeCurrencyInput(value),
		}));
	};

	const handleCurrencyFocus = (fieldName) => {
		if (isViewMode) return;
		setFormData((prev) => ({
			...prev,
			[fieldName]: normalizeCurrencyInput(prev[fieldName]),
		}));
	};

	const handleCurrencyBlur = (fieldName) => {
		setFormData((prev) => ({
			...prev,
			[fieldName]: prev[fieldName] === '' ? '' : formatCurrencyValue(prev[fieldName]),
		}));
	};

	const toDatetimeLocal = (isoString) => {
		if (!isoString) return '';
		try {
			return new Date(isoString).toISOString().slice(0, 16);
		} catch {
			return '';
		}
	};

	const toDateInput = (value) => {
		if (!value) return '';
		if (typeof value === 'string') {
			if (value.includes('T')) return value.split('T')[0];
			if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
		}
		try {
			return new Date(value).toISOString().slice(0, 10);
		} catch {
			return '';
		}
	};

	const toAmPmTime = (value) => {
		if (!value) return '';
		const str = String(value).trim().toUpperCase();
		if (/\bAM\b|\bPM\b/.test(str)) return str;
		const match = str.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
		if (!match) return value;
		let hour = parseInt(match[1], 10);
		const minute = match[2];
		const suffix = hour >= 12 ? 'PM' : 'AM';
		hour = hour % 12;
		if (hour === 0) hour = 12;
		return `${hour}:${minute} ${suffix}`;
	};

	const formatCartNumber = (value) => {
		const digits = String(value || '').replace(/\D/g, '').slice(0, 8);
		if (digits.length <= 5) {
			return digits;
		}
		return `${digits.slice(0, 5)}-${digits.slice(5)}`;
	};

	const normalizeEntryLine = (line) => ({
		...line,
		cart_number: formatCartNumber(line.cart_number),
		media_type: line.media_type ?? '',
		start_date: line.start_date ? toDateInput(line.start_date) : '',
		end_date: line.end_date ? toDateInput(line.end_date) : '',
		start_time: line.start_time ? toAmPmTime(line.start_time) : '',
		end_time: line.end_time ? toAmPmTime(line.end_time) : '',
		impressions: line.impressions ?? '',
	});

	const normalizeAsRunLine = (line) => ({
		...line,
		cart_number: formatCartNumber(line.cart_number),
		actual_run_time: line.actual_run_time ? toDatetimeLocal(line.actual_run_time) : '',
	});

	const emptyEntryLine = () => ({ cart_number: '', title: '', client: '', media_type: '', start_date: '', end_date: '', start_time: '', end_time: '', impressions: '', day_part: '', rate: '' });
	const emptyAsRunLine = () => ({ entry_line_id: '', cart_number: '', actual_run_time: '' });

	const addEntryLine = () => setEntryLines((prev) => (isViewMode ? prev : [...prev, emptyEntryLine()]));
	const removeEntryLine = (index) => setEntryLines((prev) => (isViewMode ? prev : prev.filter((_, i) => i !== index)));
	const updateEntryLine = (index, field, value) => setEntryLines((prev) => {
		if (isViewMode) return prev;
		const updated = [...prev];
		updated[index] = { ...updated[index], [field]: value };
		return updated;
	});

	const addAsRunLine = () => setAsRunLines((prev) => (isViewMode ? prev : [...prev, emptyAsRunLine()]));
	const removeAsRunLine = (index) => setAsRunLines((prev) => (isViewMode ? prev : prev.filter((_, i) => i !== index)));
	const updateAsRunLine = (index, field, value) => setAsRunLines((prev) => {
		if (isViewMode) return prev;
		const updated = [...prev];
		updated[index] = { ...updated[index], [field]: value };
		return updated;
	});

	const getInvoicePayload = () => {
		const selectedCustomerId = selectedCustomer?._id || selectedCustomer?.id || selectedCustomer?.customer_id || formData.customer_id;

		const mappedEntryLines = entryLines.map((line) => ({
			...line,
			cart_number: formatCartNumber(line.cart_number),
			impressions: parseInt(line.impressions || 0, 10),
			rate: parseFloat(line.rate || 0),
			start_date: line.start_date ? new Date(`${line.start_date}T00:00:00`).toISOString() : null,
			end_date: line.end_date ? new Date(`${line.end_date}T00:00:00`).toISOString() : null,
			start_time: line.start_time ? toAmPmTime(line.start_time) : '',
			end_time: line.end_time ? toAmPmTime(line.end_time) : '',
		}));

		const mappedAsRunLines = asRunLines.map((line) => ({
			...line,
			cart_number: formatCartNumber(line.cart_number),
			actual_run_time: line.actual_run_time ? new Date(line.actual_run_time).toISOString() : null,
		}));

		return {
			customer_id: selectedCustomerId,
            order_id: formData.order_id,
			sales_rep_email: formData.sales_rep_email,
			name: formData.name,
			order_campaign: formData.order_campaign,
			due_date: formData.due_date,
			status: formData.status,
			amount_due: parseCurrencyValue(formData.amount_due),
			total_value: parseCurrencyValue(formData.total_value),
			total_impressions: parseInt(formData.total_impressions || 0, 10),
			entry_lines: mappedEntryLines,
			as_run_lines: mappedAsRunLines,
		};
	};

	const handleCustomerChange = (customer) => {
		if (isViewMode) return;
		setSelectedCustomer(customer);
		const customerId = customer?._id || customer?.id || customer?.customer_id || '';
		setFormData((prev) => ({
			...prev,
			customer_id: customerId,
		}));
	};

	const requestWithFallback = async (method, endpoints, body) => {
		let lastError = null;

		for (const endpoint of endpoints) {
			try {
				const response = await fetch(apiUrl(endpoint), {
					method,
					headers: {
						'Content-Type': 'application/json',
					},
					body: JSON.stringify(body),
				});

				if (response.ok) {
					return response;
				}

				if (response.status === 404) {
					continue;
				}

				const errorText = await response.text();
				throw new Error(`${response.status} ${response.statusText}${errorText ? ` - ${errorText}` : ''}`);
			} catch (error) {
				lastError = error;
			}
		}

		throw lastError || new Error('Request failed for all attempted invoice endpoints');
	};

	useEffect(() => {
		const fetchCustomers = async () => {
			try {
				const response = await fetch(apiUrl('/customer/list'));
				if (!response.ok) {
					throw new Error(`Failed to load customers: ${response.status}`);
				}
				const customerList = await response.json();
				setCustomers(Array.isArray(customerList) ? customerList : []);
			} catch (error) {
				console.error('Error loading customers for invoice form:', error);
				setCustomers([]);
			}
		};

		fetchCustomers();
	}, []);

	useEffect(() => {
		if (!formData.customer_id) {
			setSelectedCustomer(null);
			return;
		}

		if (!Array.isArray(customers) || customers.length === 0) {
			return;
		}

		const targetCustomerId = String(formData.customer_id);
		const matchedCustomer = customers.find((customer) => {
			const candidateId = customer?._id || customer?.id || customer?.customer_id;
			return String(candidateId || '') === targetCustomerId;
		});

		if (matchedCustomer) {
			setSelectedCustomer(matchedCustomer);
			setCustomerComboBoxKey((prev) => prev + 1);
		}
	}, [customers, formData.customer_id]);

	useEffect(() => {
		const loadInvoice = async () => {
			if (!hasInvoiceId) {
				const pendingDraft = sessionStorage.getItem('pendingInvoiceDraft');
				if (pendingDraft) {
					try {
						const draft = JSON.parse(pendingDraft);
						setFormData((prev) => ({
							...prev,
							customer_id: draft.customer_id || prev.customer_id,
                            order_id: draft.order_id || prev.order_id,
							sales_rep_email: draft.sales_rep_email || prev.sales_rep_email,
							name: draft.name || prev.name,
							order_campaign: draft.order_campaign || prev.order_campaign,
							due_date: typeof draft.due_date === 'string' && draft.due_date.includes('T')
								? draft.due_date.split('T')[0]
								: (draft.due_date || prev.due_date),
							status: draft.status || prev.status,
							amount_due: draft.amount_due !== undefined && draft.amount_due !== null
								? formatCurrencyValue(draft.amount_due)
								: prev.amount_due,
							total_value: draft.total_value !== undefined && draft.total_value !== null
								? formatCurrencyValue(draft.total_value)
								: prev.total_value,
							total_impressions: draft.total_impressions ?? prev.total_impressions,
					}));
					setEntryLines(Array.isArray(draft.entry_lines) ? draft.entry_lines.map(normalizeEntryLine) : []);
					setAsRunLines(Array.isArray(draft.as_run_lines) ? draft.as_run_lines.map(normalizeAsRunLine) : []);
					} catch (draftError) {
						console.error('Failed to parse pending invoice draft:', draftError);
					}
					sessionStorage.removeItem('pendingInvoiceDraft');
				}
				setIsLoading(false);
				return;
			}

			setIsLoading(true);
			const readEndpoints = [
				`/invoice/one/${invoiceId}`,
				`/invoice/byid/${invoiceId}`,
				`/invoice/${invoiceId}`,
			];

			let loadedInvoice = null;
			let lastError = null;

			for (const endpoint of readEndpoints) {
				try {
					const response = await fetch(apiUrl(endpoint), {
						method: 'GET',
						headers: {
							'Content-Type': 'application/json',
						},
						mode: 'cors',
					});

					if (!response.ok) {
						if (response.status === 404) {
							continue;
						}
						const errorText = await response.text();
						throw new Error(`${response.status} ${response.statusText}${errorText ? ` - ${errorText}` : ''}`);
					}

					const responseBody = await response.json();
					loadedInvoice = responseBody?.data || responseBody;
					break;
				} catch (error) {
					lastError = error;
				}
			}

			if (!loadedInvoice) {
				alert(`Failed to load invoice: ${lastError?.message || 'Not found'}`);
				navigate('/invoices');
				setIsLoading(false);
				return;
			}

			setFormData({
				customer_id: loadedInvoice.customer_id || '',
                order_id: loadedInvoice.order_id || '',
				sales_rep_email: loadedInvoice.sales_rep_email || '',
				name: loadedInvoice.name || '',
				order_campaign: loadedInvoice.order_campaign || '',
				due_date: typeof loadedInvoice.due_date === 'string' && loadedInvoice.due_date.includes('T')
					? loadedInvoice.due_date.split('T')[0]
					: (loadedInvoice.due_date || ''),
				status: loadedInvoice.status || '',
				amount_due: loadedInvoice.amount_due !== undefined && loadedInvoice.amount_due !== null
					? formatCurrencyValue(loadedInvoice.amount_due)
					: '',
				total_value: loadedInvoice.total_value !== undefined && loadedInvoice.total_value !== null
					? formatCurrencyValue(loadedInvoice.total_value)
					: '',
				total_impressions: loadedInvoice.total_impressions ?? '',
			});
			setEntryLines(Array.isArray(loadedInvoice.entry_lines) ? loadedInvoice.entry_lines.map(normalizeEntryLine) : []);
			setAsRunLines(Array.isArray(loadedInvoice.as_run_lines) ? loadedInvoice.as_run_lines.map(normalizeAsRunLine) : []);
			setIsLoading(false);
		};

		loadInvoice();
	}, [invoiceId, hasInvoiceId, navigate]);

	const handleSubmit = async (event) => {
		event.preventDefault();
		if (isViewMode) return;
		const selectedCustomerId = selectedCustomer?._id || selectedCustomer?.id || selectedCustomer?.customer_id || formData.customer_id;

		if (!selectedCustomerId || !formData.due_date) {
			alert('Customer ID and Due Date are required');
			return;
		}

		setIsSubmitting(true);
		try {
			const payload = getInvoicePayload();

			if (isEditMode) {
				await requestWithFallback('PUT', [`/invoice/update/${invoiceId}`, '/invoice/update'], {
					...payload,
					id: invoiceId,
				});
			} else {
				await requestWithFallback('POST', ['/invoice/create', '/invoice/save'], payload);
			}

			navigate('/invoices');
		} catch (error) {
			console.error('Error saving invoice:', error);
			alert(`Failed to save invoice: ${error.message}`);
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleCancel = () => {
		navigate('/invoices');
	};

	const handleDownloadInvoicePdf = () => {
		if (!hasInvoiceId) {
			alert('Invoice must be saved before downloading PDF.');
			return;
		}

		const downloadPdf = async () => {
			try {
				const response = await fetch(apiUrl(`/invoice/pdf/${invoiceId}`), {
					method: 'GET',
					headers: {
						Accept: 'application/pdf',
					},
				});

				if (!response.ok) {
					throw new Error(`Failed to download PDF: ${response.status} ${response.statusText}`);
				}

				const pdfBlob = await response.blob();
				const pdfUrl = window.URL.createObjectURL(pdfBlob);
				const link = document.createElement('a');
				link.href = pdfUrl;
				link.download = `invoice-${invoiceId}.pdf`;
				document.body.appendChild(link);
				link.click();
				link.remove();
				window.URL.revokeObjectURL(pdfUrl);
			} catch (error) {
				console.error('Error downloading invoice PDF:', error);
				alert(`Failed to download PDF: ${error.message}`);
			}
		};

		downloadPdf();
	};

	const handleOpenDownloadModal = () => {
		if (!hasInvoiceId) {
			alert('Invoice must be saved before downloading PDF.');
			return;
		}
		setIsDownloadModalOpen(true);
	};

	const handleCloseDownloadModal = () => {
		setIsDownloadModalOpen(false);
	};

	const handleDownloadGeneral = () => {
		handleCloseDownloadModal();
		handleDownloadInvoicePdf();
	};

	const handleDownloadDetailed = () => {
		handleCloseDownloadModal();

		if (!hasInvoiceId) {
			alert('Invoice must be saved before downloading PDF.');
			return;
		}

		const downloadDetailedPdf = async () => {
			try {
				const response = await fetch(apiUrl(`/invoice/detailed/pdf/${invoiceId}`), {
					method: 'GET',
					headers: {
						Accept: 'application/pdf',
					},
				});

				if (!response.ok) {
					throw new Error(`Failed to download detailed PDF: ${response.status} ${response.statusText}`);
				}

				const pdfBlob = await response.blob();
				const pdfUrl = window.URL.createObjectURL(pdfBlob);
				const link = document.createElement('a');
				link.href = pdfUrl;
				link.download = `invoice-detailed-${invoiceId}.pdf`;
				document.body.appendChild(link);
				link.click();
				link.remove();
				window.URL.revokeObjectURL(pdfUrl);
			} catch (error) {
				console.error('Error downloading detailed invoice PDF:', error);
				alert(`Failed to download detailed PDF: ${error.message}`);
			}
		};

		downloadDetailedPdf();
	};

	const handleSendInvoice = () => {
		if (!hasInvoiceId) {
			alert('Invoice must be saved before sending.');
			return;
		}

		const amountValue = parseCurrencyValue(formData.amount_due || formData.total_value || 0);
		const amountText = new Intl.NumberFormat('en-US', {
			style: 'currency',
			currency: 'USD',
			minimumFractionDigits: 2,
			maximumFractionDigits: 2,
		}).format(amountValue);
		const invoiceLink = `${window.location.origin}/invoiceview/${invoiceId}`;
		const message = `Invoice ${invoiceId} (${formData.name || 'Invoice'}) is ready. Amount due: ${amountText}. View and pay: ${invoiceLink}`;

		const sendMethod = window.prompt('Send invoice via: email or text?', 'email');
		if (!sendMethod) return;

		const normalizedMethod = sendMethod.trim().toLowerCase();
		if (normalizedMethod === 'text' || normalizedMethod === 'sms') {
			window.open(`sms:?&body=${encodeURIComponent(message)}`, '_self');
			return;
		}

		if (normalizedMethod === 'email') {
			const recipientEmail = selectedCustomer?.contact_email || '';
			const subject = `Invoice ${invoiceId} Ready for Payment`;
			window.location.href = `mailto:${encodeURIComponent(recipientEmail)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(message)}`;
			return;
		}

		alert('Please enter "email" or "text".');
	};

	const title = isViewMode ? 'View Invoice' : (isEditMode ? 'Edit Invoice' : 'Add Invoice');

	const renderDownloadModal = () => {
		if (!isDownloadModalOpen) return null;

		return (
			<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
				<div className="w-full max-w-sm rounded-xl bg-white p-6 shadow-xl">
					<h3 className="text-lg font-semibold text-gray-900">Download Invoice</h3>
					<p className="mt-2 text-sm text-gray-600">Choose a download type.</p>
					<div className="mt-5 flex items-center justify-end gap-3">
						<button
							type="button"
							onClick={handleDownloadGeneral}
							className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500"
						>
							general
						</button>
						<button
							type="button"
							onClick={handleDownloadDetailed}
							className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500"
						>
							detailed
						</button>
						<button
							type="button"
							onClick={handleCloseDownloadModal}
							className="rounded-md bg-white px-4 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
						>
							Cancel
						</button>
					</div>
				</div>
			</div>
		);
	};

	const renderTitleBar = () => {
		if (!isViewMode) {
			return <Titlebar title={title} />;
		}

		return (
			<TitlebarWithControls title={title}>
				<div className="flex items-center gap-2">
					<button
						type="button"
						onClick={handleOpenDownloadModal}
						className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-indigo-600 text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
						aria-label="Download invoice PDF"
						title="Download PDF"
					>
						<ArrowDownTrayIcon className="h-5 w-5" aria-hidden="true" />
					</button>
					<button
						type="button"
						onClick={handleSendInvoice}
						className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-indigo-600 text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
						aria-label="Send invoice"
						title="Send to customer"
					>
						<PaperAirplaneIcon className="h-5 w-5" aria-hidden="true" />
					</button>
				</div>
			</TitlebarWithControls>
		);
	};

	if (isLoading) {
		return (
			<div className="lg:pl-72 sm:ml-0 px-0 sm:px-0">
				{renderTitleBar()}
				{renderDownloadModal()}
				<div className="m-3 px-4 sm:px-6 lg:px-8">
					<div className="flex items-center justify-center py-12">
						<div className="text-gray-500">Loading invoice...</div>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="lg:pl-72 sm:ml-0 px-0 sm:px-0">
			{renderTitleBar()}
			{renderDownloadModal()}

			<div className="m-3 px-4 sm:px-6 lg:px-8">
				<form onSubmit={handleSubmit}>
					<fieldset disabled={isViewMode}>
					<div className="space-y-10">
						<div className="border-b border-gray-900/10 pb-12">
							<div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
								<div className="sm:col-span-3 sm:col-start-1">
									<CustomerComboBox
										key={`invoice-customer-${customerComboBoxKey}-${selectedCustomer ? (selectedCustomer._id || selectedCustomer.id) : 'none'}`}
										add={true}
										addressBlock={false}
										people={customers}
										value={selectedCustomer}
										onChange={handleCustomerChange}
										disabled={isViewMode}
									/>
								</div>

								<div className="sm:col-span-3">
									<label htmlFor="sales_rep_email" className="block text-sm font-medium leading-6 text-gray-900">Sales Rep Email</label>
									<div className="mt-2">
										<input
											type="email"
											id="sales_rep_email"
											name="sales_rep_email"
											value={formData.sales_rep_email}
											onChange={handleInputChange}
											className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
										/>
									</div>
								</div>

								<div className="sm:col-span-2">
									<label htmlFor="status" className="block text-sm font-medium leading-6 text-gray-900">Status</label>
									<div className="sm:col-span-2 mt-2">
										<select
											id="status"
											name="status"
											value={formData.status}
											onChange={handleInputChange}
											className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
										>
											<option value="">Select status...</option>
											<option value="pending">Pending</option>
											<option value="open">Open</option>
											<option value="paid">Paid</option>
											<option value="past_due">Past Due</option>
											<option value="cancelled">Cancelled</option>
										</select>
									</div>
								</div>

								<div className="sm:col-span-2">
									<label htmlFor="due_date" className="block text-sm font-medium leading-6 text-gray-900">Due Date *</label>
									<div className="mt-2">
										<input
											type="date"
											id="due_date"
											name="due_date"
											value={formData.due_date}
											onChange={handleInputChange}
											required
											className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
										/>
									</div>
								</div>

								<div className="sm:col-span-3">
									<label htmlFor="order_campaign" className="block text-sm font-medium leading-6 text-gray-900">Order Campaign</label>
									<div className="mt-2">
										<input
											type="text"
											id="order_campaign"
											name="order_campaign"
											value={formData.order_campaign}
											onChange={handleInputChange}
											className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
										/>
									</div>
								</div>

								<div className="sm:col-span-2 sm:col-start-1">
									<label htmlFor="amount_due" className="block text-sm font-medium leading-6 text-gray-900">Amount Due</label>
									<div className="mt-2">
										<input
											type="text"
											inputMode="decimal"
											id="amount_due"
											name="amount_due"
											value={formData.amount_due}
											onChange={handleCurrencyChange}
											onFocus={() => handleCurrencyFocus('amount_due')}
											onBlur={() => handleCurrencyBlur('amount_due')}
											className="block w-full rounded-md border-0 bg-green-50 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
										/>
									</div>
								</div>

								<div className="sm:col-span-2">
									<label htmlFor="total_value" className="block text-sm font-medium leading-6 text-gray-900">Total Value</label>
									<div className="mt-2">
										<input
											type="text"
											inputMode="decimal"
											id="total_value"
											name="total_value"
											value={formData.total_value}
											onChange={handleCurrencyChange}
											onFocus={() => handleCurrencyFocus('total_value')}
											onBlur={() => handleCurrencyBlur('total_value')}
											className="block w-full rounded-md border-0 bg-green-50 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
										/>
									</div>
								</div>

								<div className="sm:col-span-2">
									<label htmlFor="total_impressions" className="block text-sm font-medium leading-6 text-gray-900">Total Impressions</label>
									<div className="mt-2">
										<input
											type="number"
											id="total_impressions"
											name="total_impressions"
											value={formData.total_impressions}
											onChange={handleInputChange}
											className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
										/>
									</div>
								</div>

								<div className="sm:col-span-6">
									<div className="flex items-center justify-between mb-2">
										<h3 className="text-sm font-medium leading-6 text-gray-900">Entry Lines</h3>
										{!isViewMode && (
										<button
											type="button"
											onClick={addEntryLine}
											className="rounded-md bg-indigo-600 px-2 py-1 text-xs font-semibold text-white shadow-sm hover:bg-indigo-500"
										>
											+ Add Line
										</button>
										)}
									</div>
									<div className="overflow-x-auto rounded-md ring-1 ring-gray-300">
										<table className="min-w-full divide-y divide-gray-200 text-xs">
											<thead className="bg-gray-50">
												<tr>
                                                    <th className="px-2 py-2 text-left font-medium text-gray-500">Cart #</th>
                                                    <th className="px-2 py-2 text-left font-medium text-gray-500">Media Type</th>
                                                    <th className="px-2 py-2 text-left font-medium text-gray-500">Client</th>
                                                    <th className="px-2 py-2 text-left font-medium text-gray-500">Start Date</th>
                                                    <th className="px-2 py-2 text-left font-medium text-gray-500">End Date</th>
                                                    <th className="px-2 py-2 text-left font-medium text-gray-500">Start Time</th>
                                                    <th className="px-2 py-2 text-left font-medium text-gray-500">End Time</th>
                                                    <th className="px-2 py-2 text-left font-medium text-gray-500">Impressions</th>
												</tr>
											</thead>
											<tbody className="divide-y divide-gray-100 bg-white">
												{entryLines.length === 0 && (
													<tr>
															<td colSpan={8} className="px-4 py-3 text-center text-gray-400 italic">No entry lines. Click &quot;+ Add Line&quot; to add one.</td>
													</tr>
												)}
												{entryLines.map((line, index) => (
													<tr key={index}>
														<td className="px-2 py-1">
															<input type="text" value={line.cart_number} onChange={(e) => updateEntryLine(index, 'cart_number', formatCartNumber(e.target.value))}
																placeholder="12345-678"
																maxLength={9}
																className="w-20 rounded border-0 py-1 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 text-xs" />
														</td>
														<td className="px-2 py-1">																	<input type="text" value={line.media_type} onChange={(e) => updateEntryLine(index, 'media_type', e.target.value)}
																		className="w-20 rounded border-0 py-1 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 text-xs" />
														</td>
														<td className="px-2 py-1">
															<input type="text" value={line.client} onChange={(e) => updateEntryLine(index, 'client', e.target.value)}
																className="w-28 rounded border-0 py-1 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 text-xs" />
														</td>
														<td className="px-2 py-1">															
                                                            <input type="date" value={line.start_date} onChange={(e) => updateEntryLine(index, 'start_date', e.target.value)}
																className="rounded border-0 py-1 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 text-xs" />
														</td>
														<td className="px-2 py-1">
															<input type="date" value={line.end_date} onChange={(e) => updateEntryLine(index, 'end_date', e.target.value)}
																className="rounded border-0 py-1 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 text-xs" />
														</td>
														<td className="px-2 py-1">
															<input type="text" value={line.start_time} onChange={(e) => updateEntryLine(index, 'start_time', e.target.value)} onBlur={(e) => updateEntryLine(index, 'start_time', toAmPmTime(e.target.value))}
																placeholder="h:mm AM/PM"
															className="w-20 rounded border-0 py-1 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 text-xs" />
													</td>
													<td className="px-2 py-1">
															<input type="text" value={line.end_time} onChange={(e) => updateEntryLine(index, 'end_time', e.target.value)} onBlur={(e) => updateEntryLine(index, 'end_time', toAmPmTime(e.target.value))}
																placeholder="h:mm AM/PM"
															className="w-20 rounded border-0 py-1 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 text-xs" />
													</td>
													<td className="px-2 py-1">
														<input type="number" value={line.impressions} onChange={(e) => updateEntryLine(index, 'impressions', e.target.value)}
															className="w-16 rounded border-0 py-1 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 text-xs" />
														</td>
													</tr>
												))}
											</tbody>
										</table>
									</div>
								</div>

								<div className="sm:col-span-6">
									<div className="flex items-center justify-between mb-2">
										<h3 className="text-sm font-medium leading-6 text-gray-900">As Run Lines</h3>
										{!isViewMode && (
										<button
											type="button"
											onClick={addAsRunLine}
											className="rounded-md bg-indigo-600 px-2 py-1 text-xs font-semibold text-white shadow-sm hover:bg-indigo-500"
										>
											+ Add Line
										</button>
										)}
									</div>
									<div className="overflow-x-auto rounded-md ring-1 ring-gray-300">
										<table className="min-w-full divide-y divide-gray-200 text-xs">
											<thead className="bg-gray-50">
												<tr>
													<th className="px-2 py-2 text-left font-medium text-gray-500">Entry Line ID</th>
													<th className="px-2 py-2 text-left font-medium text-gray-500">Cart #</th>
													<th className="px-2 py-2 text-left font-medium text-gray-500">Actual Run Time</th>
													<th className="px-2 py-2"></th>
												</tr>
											</thead>
											<tbody className="divide-y divide-gray-100 bg-white">
												{asRunLines.length === 0 && (
													<tr>
														<td colSpan={4} className="px-4 py-3 text-center text-gray-400 italic">No as run lines. Click &quot;+ Add Line&quot; to add one.</td>
													</tr>
												)}
												{asRunLines.map((line, index) => (
													<tr key={index}>
														<td className="px-2 py-1">
															<input type="text" value={line.entry_line_id} onChange={(e) => updateAsRunLine(index, 'entry_line_id', e.target.value)}
																className="w-40 rounded border-0 py-1 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 text-xs" />
														</td>
														<td className="px-2 py-1">
															<input type="text" value={line.cart_number} onChange={(e) => updateAsRunLine(index, 'cart_number', formatCartNumber(e.target.value))}
																placeholder="12345-678"
																maxLength={9}
																className="w-20 rounded border-0 py-1 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 text-xs" />
														</td>
														<td className="px-2 py-1">
															<input type="datetime-local" value={line.actual_run_time} onChange={(e) => updateAsRunLine(index, 'actual_run_time', e.target.value)}
																className="rounded border-0 py-1 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 text-xs" />
														</td>
														<td className="px-2 py-1">
															{!isViewMode && (
															<button type="button" onClick={() => removeAsRunLine(index)}
																className="text-red-500 hover:text-red-700 font-bold text-sm px-1">✕</button>
															)}
														</td>
													</tr>
												))}
											</tbody>
										</table>
									</div>
								</div>
							</div>
						</div>
					</div>
					</fieldset>

					<div className="mt-6 flex items-center justify-end gap-x-3">
						<button
							type="button"
							onClick={handleCancel}
							className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
						>
							{isViewMode ? 'Close' : 'Cancel'}
						</button>
						{!isViewMode && (
							<button
								type="submit"
								disabled={isSubmitting}
								className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:opacity-60"
							>
								{isSubmitting ? 'Saving...' : (isEditMode ? 'Update Invoice' : 'Create Invoice')}
							</button>
						)}
					</div>
				</form>
			</div>
		</div>
	);
}

export default InvoiceForm;
